import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Image, Pressable, Alert, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';
import { 
  PageContainer, 
  ContentContainer, 
  BottomContainer,
  HeaderContainer,
  Text, 
  Input,
  Button,
  Card,
  Theme 
} from '../../components/ui';
import { Ionicons } from '@expo/vector-icons';
import { auth } from '../../config/firebase';
import { PrivateProfileService } from '../../services/privateProfileService';
import Toast from 'react-native-toast-message';

const EditProfileScreen: React.FC = () => {
  const navigation = useNavigation();
  const [displayName, setDisplayName] = useState('');
  const [avatarUri, setAvatarUri] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadCurrentProfile();
  }, []);

  const loadCurrentProfile = async () => {
    setIsLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      // Load display name from auth
      setDisplayName(currentUser.displayName || '');

      // Try to load avatar from profile userData
      const profileResult = await PrivateProfileService.getProfile(currentUser.uid);
      if (profileResult.success && profileResult.data?.userData?.avatarUrl) {
        setAvatarUri(profileResult.data.userData.avatarUrl);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePickImage = async () => {
    try {
      // Request permissions
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
      
      if (permissionResult.granted === false) {
        Alert.alert('Permission Required', 'Please grant camera roll permissions to change your photo.');
        return;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        setAvatarUri(result.assets[0].uri);
        setHasChanges(true);
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to select image. Please try again.');
    }
  };

  const handleDisplayNameChange = (text: string) => {
    setDisplayName(text);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!auth.currentUser) {
      Alert.alert('Error', 'No authenticated user found.');
      return;
    }

    setIsSaving(true);
    try {
      // Update Firebase Auth profile
      if (typeof window !== 'undefined') {
        // Web
        const { updateProfile } = require('firebase/auth');
        await updateProfile(auth.currentUser, {
          displayName: displayName.trim() || null,
          // photoURL: avatarUri || null, // TODO: Upload to storage first
        });
      } else {
        // Native
        await auth.currentUser.updateProfile({
          displayName: displayName.trim() || null,
          // photoURL: avatarUri || null, // TODO: Upload to storage first
        });
      }

      // Update PrivateProfile
      const updates: any = {
        displayName: displayName.trim() || undefined,
        userData: {
          avatarUrl: avatarUri || undefined,
        },
      };

      await PrivateProfileService.updateProfile(auth.currentUser.uid, updates);

      // Show success feedback
      if (Platform.OS === 'web') {
        Toast.show({
          type: 'success',
          text1: 'Profile updated',
          text2: 'Your changes have been saved.',
        });
      } else {
        Alert.alert('Success', 'Your profile has been updated.');
      }

      setHasChanges(false);
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigation.goBack();
      }, 500);
    } catch (error) {
      console.error('Error saving profile:', error);
      
      if (Platform.OS === 'web') {
        Toast.show({
          type: 'error',
          text1: 'Save failed',
          text2: error instanceof Error ? error.message : 'Unknown error',
        });
      } else {
        Alert.alert(
          'Error',
          `Failed to save profile: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (Platform.OS === 'web') {
        if (window.confirm('Discard changes?')) {
          navigation.goBack();
        }
      } else {
        Alert.alert(
          'Discard Changes?',
          'You have unsaved changes. Are you sure you want to go back?',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Discard', style: 'destructive', onPress: () => navigation.goBack() },
          ]
        );
      }
    } else {
      navigation.goBack();
    }
  };

  return (
    <PageContainer>
      <View style={styles.header}>
        <Pressable style={styles.backButton} onPress={handleCancel}>
          <Ionicons name="arrow-back" size={24} color={Theme.colors.textPrimary} />
        </Pressable>
        <Text variant="heading3" style={styles.headerTitle}>
          Edit Profile
        </Text>
        <View style={styles.placeholder} />
      </View>

      <ContentContainer>
        <HeaderContainer>
          <Text variant="body2" color="secondary" align="center">
            Update your profile information
          </Text>
        </HeaderContainer>

        <Card style={styles.card}>
          {/* Avatar Section */}
          <View style={styles.avatarSection}>
            <View style={styles.avatarContainer}>
              {avatarUri ? (
                <Image source={{ uri: avatarUri }} style={styles.avatarImage} />
              ) : (
                <View style={styles.avatarPlaceholder}>
                  <Ionicons name="person" size={60} color={Theme.colors.textSecondary} />
                </View>
              )}
            </View>
            <Pressable onPress={handlePickImage} style={styles.changePhotoButton}>
              <Text variant="body1" style={styles.changePhotoText}>
                Change photo
              </Text>
            </Pressable>
          </View>

          <View style={styles.divider} />

          {/* Display Name Section */}
          <View style={styles.inputSection}>
            <Text variant="body2" color="secondary" style={styles.label}>
              Display Name
            </Text>
            <Input
              value={displayName}
              onChangeText={handleDisplayNameChange}
              placeholder="Enter your name"
              maxLength={40}
              autoCapitalize="words"
              disabled={isSaving}
            />
          </View>

          {/* Email (Read-only) */}
          <View style={styles.inputSection}>
            <Text variant="body2" color="secondary" style={styles.label}>
              Email
            </Text>
            <View style={styles.readOnlyField}>
              <Text variant="body1" color="secondary">
                {auth.currentUser?.email || 'No email'}
              </Text>
            </View>
          </View>
        </Card>

        <View style={styles.note}>
          <Text variant="caption" color="secondary" align="center">
            Your email is managed through your account settings and cannot be changed here.
          </Text>
        </View>
      </ContentContainer>

      <BottomContainer>
        <Button
          title={isSaving ? 'Saving...' : 'Save Changes'}
          onPress={handleSave}
          disabled={!hasChanges || isSaving || isLoading}
        />
      </BottomContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.md,
    backgroundColor: Theme.colors.surface,
    borderBottomWidth: 0.5,
    borderBottomColor: Theme.colors.border,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  placeholder: {
    width: 40,
  },
  card: {
    padding: Theme.spacing.xl,
  },
  avatarSection: {
    alignItems: 'center',
    marginBottom: Theme.spacing.lg,
  },
  avatarContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    overflow: 'hidden',
    marginBottom: Theme.spacing.md,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: Theme.colors.surfaceSecondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  changePhotoButton: {
    paddingVertical: Theme.spacing.sm,
    paddingHorizontal: Theme.spacing.lg,
  },
  changePhotoText: {
    color: Theme.colors.primary,
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  divider: {
    height: 1,
    backgroundColor: Theme.colors.border,
    marginVertical: Theme.spacing.lg,
  },
  inputSection: {
    marginBottom: Theme.spacing.lg,
  },
  label: {
    marginBottom: Theme.spacing.sm,
  },
  readOnlyField: {
    borderRadius: Theme.borderRadius.md,
    backgroundColor: Theme.colors.surfaceSecondary,
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.md,
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  note: {
    marginTop: Theme.spacing.lg,
    paddingHorizontal: Theme.spacing.lg,
  },
});

export default EditProfileScreen;

